import React, { useContext, useEffect, useState } from 'react';

import { TaskEntity } from '../../tasks/Task.interface';
import {
  useCreateProductValidation,
  useProductEntityPrefixId,
  useProductViewParams,
  useUpdateProductValidation
} from '../Product.hooks';
import {
  BaseProductBoardColumnProps,
  ProductValidation
} from '../Product.interface';
import { ProductBoardContext } from './ProductBoard.context';
import { ProductBoardColumnEntity } from './ProductBoardColumn';
import ProductBoardItem from './ProductBoardItem';

export type ProductBoardValidationProps = BaseProductBoardColumnProps<
  ProductValidation,
  'validation'
>;

const ProductBoardValidation: React.FC<ProductBoardValidationProps> = (
  props: ProductBoardValidationProps
) => {
  const {
    product,
    collapsed,
    validation: initialValidation,
    onCancel,
    onError,
    onSave
  } = props;
  const entity = ProductBoardColumnEntity.Validation;
  const params: string = useProductViewParams();
  const { toggleSelected, isSelected } = useContext(ProductBoardContext);
  const [validation, setValidation] = useState(initialValidation);
  useEffect(() => {
    if (initialValidation) {
      setValidation(initialValidation);
    }
  }, [initialValidation]);
  const {
    createdProductValidation,
    isCreatingProductValidation,
    createProductValidationError,
    createProductValidation
  } = useCreateProductValidation(product.id);
  const {
    updatedProductValidation,
    isUpdatingProductValidation,
    updateProductValidation
  } = useUpdateProductValidation(product.id, validation.id || 0);
  const entityId: string = useProductEntityPrefixId(
    validation,
    ProductBoardColumnEntity.Validation
  );

  useEffect(() => {
    if (createdProductValidation) {
      setValidation(createdProductValidation);
      onSave?.(createdProductValidation, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdProductValidation]);

  useEffect(() => {
    if (updatedProductValidation) {
      setValidation(updatedProductValidation);
      onSave?.(updatedProductValidation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductValidation]);

  useEffect(() => {
    if (createProductValidationError) {
      onError?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProductValidationError]);

  return (
    <ProductBoardItem
      product={product}
      taskEntity={validation as TaskEntity}
      title={validation.title || ''}
      entityId={entityId}
      collapsed={collapsed}
      editable={!validation.id}
      isSelected={isSelected(validation.id, entity)}
      loading={isUpdatingProductValidation || isCreatingProductValidation}
      viewDetailsLink={`/portal/products/${validation.productId}/validations/${validation.id}${params}`}
      onSelect={() => toggleSelected(validation, entity)}
      onCancel={() => {
        setValidation(initialValidation);
        onCancel?.();
      }}
      onSave={title => {
        validation.title = title;
        if (validation.id) {
          updateProductValidation(validation);
        } else {
          createProductValidation(validation);
        }
      }}
    />
  );
};

export default ProductBoardValidation;
